// Comportamiento del navegador del espacio de Contactos (ContactWorkspace.razor.js del portal actual), con los mismos
// selectores y el mismo orden: ficha, carriles de avisos, toques largos, Escape, menús anclados, hojas del celular,
// carga incremental y vuelta a la ventana. Los datos y la autoridad los decide el servidor; esto solo mueve foco y cajas.

export type WorkspaceHandlers = {
  longSelect(id: number): void;
  navigateFromEditor(path: string): void;
  windowReturned(): void;
  importClosed(): void;
  reportColumns(columns: string[], automatic: boolean): Promise<void>;
  dismiss(): void;
  contentWidth(width: number, viewport: number): void;
  loadMore(): void;
};

/** Corre fn después del próximo layout y antes de pintar (primer aviso de un ResizeObserver creado en un cuadro). */
export function afterLayout(fn: () => void): void {
  requestAnimationFrame(() => {
    if (typeof ResizeObserver === "undefined") {
      fn();
      return;
    }
    const observer = new ResizeObserver(() => {
      observer.disconnect();
      fn();
    });
    observer.observe(document.documentElement);
  });
}

/** Una variable con el mismo valor no se vuelve a escribir (cada escritura invalida los estilos de la lista). */
function setVar(element: HTMLElement | null, name: string, value: string) {
  if (element && element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
}

const surfaceVars = new Map<string, string>();
let current: { root: HTMLElement; dispose: () => void } | null = null;

const small = () => matchMedia("(max-width:767px)").matches;
const fine = () => matchMedia("(pointer:fine)").matches && matchMedia("(min-width:768px)").matches;

/** Ubica un menú junto al botón que lo abrió: debajo si entra; si no, encima. En el celular, la hoja ocupa la pantalla. */
export function placeMenu(panel: HTMLElement, scope: ParentNode = document): void {
  if (panel.classList.contains("c1-sheet") && small()) {
    panel.style.removeProperty("left");
    panel.style.removeProperty("top");
    return;
  }
  const trigger =
    document.getElementById(panel.dataset.contactPopoverTrigger ?? "") ??
    scope.querySelector(`[popovertarget="${panel.id}"]`);
  if (!trigger) return;
  const anchor = trigger.getBoundingClientRect(),
    box = panel.getBoundingClientRect();
  panel.style.left = Math.max(8, Math.min(anchor.left, window.innerWidth - box.width - 8)) + "px";
  const below = anchor.bottom + 4,
    above = anchor.top - 4 - box.height;
  const top =
    below + box.height <= window.innerHeight - 8
      ? below
      : above >= 8
        ? above
        : Math.max(8, window.innerHeight - box.height - 8);
  panel.style.top = top + "px";
}

/** Abre o cierra un menú emergente y lo mantiene ubicado mientras carga sus opciones (togglePopover). */
export function togglePopover(panel: HTMLElement | null): void {
  if (!panel) return;
  panel.togglePopover();
  if (!panel.matches(":popover-open") || !panel.matches("[data-contact-popover]")) return;
  placeMenu(panel);
  const follow = new ResizeObserver(() => {
    if (!panel.isConnected) {
      follow.disconnect();
      return;
    }
    if (panel.matches(":popover-open")) placeMenu(panel);
  });
  follow.observe(panel);
  const done = (event: Event) => {
    if ((event as ToggleEvent).newState !== "closed") return;
    follow.disconnect();
    panel.removeEventListener("toggle", done);
  };
  panel.addEventListener("toggle", done);
}

export function hidePopover(panel: HTMLElement | null): void {
  if (panel?.matches(":popover-open")) panel.hidePopover();
}

function scrollHost(): Element | null {
  const root = current?.root;
  const directory = [...(root?.querySelectorAll(".c1-table-scroll,.c2-directory") ?? [])].find(
    (item) => getComputedStyle(item).display !== "none" && item.scrollHeight > item.clientHeight,
  );
  if (directory) return directory;
  for (let element = root?.parentElement; element && element !== document.body; element = element.parentElement)
    if (["auto", "scroll"].includes(getComputedStyle(element).overflowY) && element.scrollHeight > element.clientHeight)
      return element;
  return document.scrollingElement;
}

export const readScroll = () => scrollHost()?.scrollTop ?? 0;

/** Después del layout y solo si cambia: volver a la lista arriba de todo no escribe nada. */
export function restoreScroll(value: number): void {
  afterLayout(() => {
    const host = scrollHost();
    if (host && Math.abs(host.scrollTop - value) >= 1) host.scrollTo({ top: value, behavior: "instant" });
  });
}

export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export const currentPath = () => location.pathname + location.search + location.hash;

export function downloadText(value: string, name: string): void {
  const url = URL.createObjectURL(new Blob(["\uFEFF", value], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Descarga en flujo: junta los bloques en un Blob y cuenta los registros (sin la cabecera). */
export async function downloadStream(name: string, body: ReadableStream<Uint8Array>): Promise<number> {
  const reader = body.getReader();
  const decoder = new TextDecoder("utf-8");
  const chunks: Uint8Array[] = [];
  let quoted = false,
    rows = 0,
    lastWasNewline = true;
  const scan = (text: string) => {
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '"') {
        if (quoted && text[i + 1] === '"') i++;
        else quoted = !quoted;
        lastWasNewline = false;
      } else if (ch === "\n" && !quoted) {
        rows++;
        lastWasNewline = true;
      } else if (ch !== "\r") lastWasNewline = false;
    }
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    scan(decoder.decode(value, { stream: true }));
  }
  scan(decoder.decode());
  if (!lastWasNewline && chunks.length) rows++;
  // El servidor ya escribe el BOM de UTF-8: se agrega solo si no viene (con dos, Excel lee «\uFEFFNombre»).
  const first = chunks[0];
  const hasBom = !!first && first.length >= 3 && first[0] === 0xef && first[1] === 0xbb && first[2] === 0xbf;
  const parts = (hasBom ? chunks : ["\uFEFF", ...chunks]) as BlobPart[];
  const url = URL.createObjectURL(new Blob(parts, { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return Math.max(0, rows - 1);
}

/** Al cerrar la ficha, el foco vuelve a su fila sin mover el desplazamiento. */
export function focusRow(id: number): void {
  const row = current?.root.querySelector(`[data-contact-row="${id}"]`);
  const target = [...(row?.querySelectorAll<HTMLElement>("a[data-contact-open]") ?? [])].find(
    (item) => item.getClientRects().length,
  );
  (target ?? (row as HTMLElement | null))?.focus({ preventScroll: true });
}

export function focusField(id: string): void {
  const field = document.getElementById(id);
  field?.closest("details")?.setAttribute("open", "");
  field?.focus();
}

export function replacePath(path: string): void {
  history.replaceState(history.state, "", path);
}

/** Ordenar, filtrar y paginar quedan en el historial y llevan la lista arriba, sin pedir el documento al servidor. */
export function pushPath(path: string): void {
  // Una entrada nueva con su propia clave, como las del enrutador, para que Atrás y Adelante la reconozcan.
  const previous = (history.state ?? {}) as { idx?: number };
  history.pushState(
    { ...previous, key: Math.random().toString(36).slice(2, 10), idx: (previous.idx ?? 0) + 1 },
    "",
    path,
  );
  const host = scrollHost();
  if (host) host.scrollTop = 0;
}

/** Enlaza el espacio de trabajo: un solo espacio activo, como el módulo del portal actual. */
export function bindWorkspace(element: HTMLElement, handlers: WorkspaceHandlers): () => void {
  current?.dispose();
  const abort = new AbortController();
  const signal = abort.signal;
  const root = element;
  // La isla reemplaza el h1 que enfocó la navegación: se enfoca una vez, después del layout, si el foco no está en otro lado.
  afterLayout(() => {
    const active = document.activeElement;
    if (current?.root !== element || (active && active !== document.body)) return;
    const title = element.querySelector<HTMLElement>("h1");
    if (!title?.focus) return;
    if (!title.hasAttribute("tabindex")) title.setAttribute("tabindex", "-1");
    title.focus({ preventScroll: true });
  });
  // El botón «Chats» del Dock: en el portal nuevo el Dock puede montarse después de esta pantalla, así que se busca cada
  // vez y su posición original se guarda la primera vez que aparece (en el portal actual llega prerenderizado).
  let launcher: HTMLElement | null = null;
  let originalRight = "";
  // Cuándo el Dock oculta su botón (pantalla completa o ventana abierta) lo decide el Dock (dock.tsx) y se lee en cada
  // pasada, nunca copiado al arrancar: viniendo de Conversaciones, esta pantalla arranca antes de que el Dock salga de
  // pantalla completa, y una copia vieja dejaba el botón oculto para siempre (React no vuelve a escribir un valor igual).
  const dockHidesLauncher = (item: HTMLElement) => {
    const host = item.closest<HTMLElement>(".chat-dock-window");
    return !!host && (host.dataset.fullscreen === "true" || host.dataset.mode !== "minimized");
  };
  const findLauncher = () => {
    if (launcher?.isConnected) return launcher;
    launcher = document.querySelector<HTMLElement>(".chat-dock-launcher");
    if (launcher) originalRight = launcher.style.right;
    return launcher;
  };
  const initialLauncher = findLauncher();
  for (const [name, value] of surfaceVars)
    if (name !== "--c1-dock-width" || (initialLauncher && !initialLauncher.hidden)) setVar(root, name, value);
  const setSurfaceVar = (name: string, value: string) => {
    surfaceVars.set(name, value);
    setVar(root, name, value);
  };
  const inertBefore = new Map<HTMLElement, boolean>();
  const restoreInert = () => {
    for (const [item, value] of inertBefore) item.inert = value;
    inertBefore.clear();
  };
  let lastPanel: Element | null = null,
    hold = 0,
    press: { x: number; y: number } | null = null,
    skipClick = false,
    pointerOpen = false,
    placing = 0;
  // El panel empieza a la altura del encabezado de la tabla y nunca por encima de la barra superior del portal.
  const placePanel = () => {
    const head = root.querySelector(".c2-contact-table thead");
    const theadTop =
      head && head.getClientRects().length
        ? head.getBoundingClientRect().top + scrollY - (visualViewport?.offsetTop ?? 0)
        : 0;
    const shellBottom = Math.max(0, document.querySelector(".shell-topbar")?.getBoundingClientRect().bottom ?? 0);
    if (theadTop > 0) setVar(root, "--c1-panel-top", Math.max(theadTop, shellBottom) + "px");
  };
  const syncSurfaces = () => {
    const panel = root.querySelector<HTMLElement>(".c1-detail-panel");
    const isSmall = small();
    const keyboard = Math.max(
      0,
      innerHeight - (visualViewport?.height ?? innerHeight) - (visualViewport?.offsetTop ?? 0),
    );
    const editor = !!root.querySelector(".c1-editor");
    const toastVisible = [...root.querySelectorAll(".feedback-toast")].some((item) => {
      const style = getComputedStyle(item);
      return style.visibility !== "hidden" && +style.opacity > 0;
    });
    setSurfaceVar("--c1-toast-lane", !editor && toastVisible ? "48px" : "0px");
    // En el celular la ficha ocupa la pantalla: lo de atrás no recibe foco ni acciones hasta cerrarla.
    restoreInert();
    if (panel && isSmall) {
      const targets = [...root.children].filter((item) => item !== panel && !item.contains(panel)) as HTMLElement[];
      targets.push(...document.querySelectorAll<HTMLElement>(".shell-topbar,.shell-sidebar"));
      for (const item of targets) {
        if (!inertBefore.has(item)) inertBefore.set(item, item.inert);
        item.inert = true;
      }
    }
    if (panel) placePanel();
    setSurfaceVar("--c1-keyboard-offset", keyboard + "px");
    setSurfaceVar("--c1-toast-offset", keyboard + (editor && isSmall ? 90 : 60) + "px");
    const dock = findLauncher();
    if (dock) {
      const right = panel && !isSmall ? panel.getBoundingClientRect().width + 12 + "px" : originalRight;
      if (dock.style.right !== right) dock.style.right = right;
      const fixedBar = isSmall && !!root.querySelector(".c1-editor-actions,dialog.c1-import[open]");
      const hidden = dockHidesLauncher(dock) || keyboard > 120 || !!(panel && isSmall) || fixedBar;
      if (dock.hidden !== hidden) dock.hidden = hidden;
      const lane = dock.getBoundingClientRect();
      if (!dock.hidden && lane.width > 0) setSurfaceVar("--c1-dock-width", innerWidth - lane.left + "px");
    }
    if (panel) {
      panel.setAttribute("role", "dialog");
      panel.setAttribute("aria-modal", isSmall ? "true" : "false");
      // Al abrir, el foco va a la × (en el celular, a ←); abierta con el mouse, el anillo no se ve hasta usar el teclado.
      if (panel !== lastPanel) {
        const close = [...panel.querySelectorAll<HTMLElement>(".c1-panel-close,.c1-panel-mobile-back")].find(
          (item) => item.getClientRects().length,
        );
        if (pointerOpen) panel.dataset.focusQuiet = "true";
        else delete panel.dataset.focusQuiet;
        (close ?? panel).focus({ preventScroll: true });
      }
    }
    lastPanel = panel;
    root.querySelectorAll<HTMLInputElement>("thead input[type=checkbox]").forEach((box) => {
      box.indeterminate = box.dataset.mixed === "true" && !box.checked;
    });
  };
  let surfacesQueued = false;
  const queueSurfaces = () => {
    if (surfacesQueued) return;
    surfacesQueued = true;
    afterLayout(() => {
      surfacesQueued = false;
      if (!signal.aborted) syncSurfaces();
    });
  };
  const surfaceObserver = new MutationObserver(queueSurfaces);
  surfaceObserver.observe(root, { childList: true, subtree: true });
  // Cuando el Dock se monta (solo sus hijos directos, no cada cambio del chat), el botón «Chats» toma su lugar.
  const dockRoot = document.getElementById("bheli-chat-root");
  if (dockRoot) surfaceObserver.observe(dockRoot, { childList: true });
  document.addEventListener(
    "scroll",
    () => {
      if (placing || !root.querySelector(".c1-detail-panel")) return;
      placing = requestAnimationFrame(() => {
        placing = 0;
        if (!signal.aborted) placePanel();
      });
    },
    { capture: true, passive: true, signal },
  );
  visualViewport?.addEventListener("resize", syncSurfaces, { signal });
  window.addEventListener("resize", syncSurfaces, { signal });
  root.addEventListener(
    "pointerdown",
    () => {
      pointerOpen = true;
    },
    { capture: true, signal },
  );
  document.addEventListener(
    "keydown",
    () => {
      pointerOpen = false;
      delete root.querySelector<HTMLElement>(".c1-detail-panel")?.dataset.focusQuiet;
    },
    { capture: true, signal },
  );
  root.addEventListener(
    "pointerdown",
    (event) => {
      const target = event.target as Element;
      if (event.pointerType === "mouse" || target.closest("button,input")) return;
      const row = target.closest<HTMLElement>("[data-contact-row]");
      if (!row) return;
      press = { x: event.clientX, y: event.clientY };
      hold = window.setTimeout(() => {
        skipClick = true;
        handlers.longSelect(Number(row.dataset.contactRow));
      }, 500);
    },
    { signal },
  );
  const cancelHold = () => clearTimeout(hold);
  root.addEventListener(
    "pointermove",
    (event) => {
      if (press && Math.hypot(event.clientX - press.x, event.clientY - press.y) > 8) cancelHold();
    },
    { signal },
  );
  root.addEventListener("pointerup", cancelHold, { signal });
  root.addEventListener("pointercancel", cancelHold, { signal });
  root.addEventListener(
    "click",
    (event) => {
      if (skipClick) {
        event.preventDefault();
        event.stopImmediatePropagation();
        skipClick = false;
      }
    },
    { capture: true, signal },
  );
  // El nombre abre la ficha aquí, sin navegar; con Ctrl, Shift o el botón del medio sigue siendo un enlace.
  root.addEventListener(
    "click",
    (event) => {
      const link = (event.target as Element).closest?.("a[data-contact-open]");
      if (link && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey)
        event.preventDefault();
    },
    { capture: true, signal },
  );
  // Un enlace con el editor con cambios pasa por la confirmación de descartar.
  document.addEventListener(
    "click",
    (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      if (!root.querySelector('.c1-editor[data-dirty="true"]')) return;
      const link = (event.target as Element).closest<HTMLAnchorElement>("a[href]");
      if (!link || link.target === "_blank" || link.hasAttribute("download")) return;
      const destination = new URL(link.href, location.href);
      if (
        destination.origin !== location.origin ||
        (destination.pathname === location.pathname && destination.search === location.search)
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
      handlers.navigateFromEditor(destination.pathname + destination.search + destination.hash);
    },
    { capture: true, signal },
  );
  root.addEventListener(
    "keydown",
    (event) => {
      const panel = root.querySelector(".c1-detail-panel");
      if (event.key !== "Tab" || !panel || !small()) return;
      const items = [
        ...panel.querySelectorAll<HTMLElement>("a[href],button:not(:disabled),input:not(:disabled),summary"),
      ].filter((item) => item.getClientRects().length);
      if (!items.length) return;
      if (event.shiftKey && (document.activeElement === items[0] || document.activeElement === panel)) {
        items.at(-1)!.focus();
        event.preventDefault();
      } else if (!event.shiftKey && document.activeElement === items.at(-1)) {
        items[0]!.focus();
        event.preventDefault();
      }
    },
    { signal },
  );
  queueSurfaces();
  root.addEventListener("animationend", syncSurfaces, { signal });
  window.addEventListener("focus", () => handlers.windowReturned(), { signal });
  document.addEventListener(
    "visibilitychange",
    () => {
      if (!document.hidden) handlers.windowReturned();
    },
    { signal },
  );
  root.addEventListener(
    "close",
    (event) => {
      if ((event.target as Element).matches?.("dialog.c1-import")) handlers.importClosed();
    },
    { capture: true, signal },
  );
  root.addEventListener(
    "reportviewapply",
    (event) => {
      const detail = (event as CustomEvent).detail as {
        reportId: string;
        columns: string[];
        automatic?: boolean;
        pending?: Promise<unknown>[];
      };
      if (detail?.reportId !== "chat.contacts") return;
      const completion = handlers.reportColumns(detail.columns, detail.automatic === true).catch(() => undefined);
      detail.pending?.push(completion);
    },
    { signal },
  );
  root.addEventListener(
    "keydown",
    (event) => {
      const target = event.target as HTMLElement;
      const menu = target.closest?.('[data-contact-popover][role="menu"]');
      if (event.key === "Escape" && target.matches("input[type=search]") && (target as HTMLInputElement).value) {
        event.preventDefault();
        event.stopImmediatePropagation();
        // El valor vacío lo pone quien escucha «input» (la búsqueda de la lista o la del selector).
        const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")!.set!;
        setter.call(target, "");
        target.dispatchEvent(new Event("input", { bubbles: true }));
        return;
      }
      if (menu && ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        const items = [...menu.querySelectorAll<HTMLElement>("button:not([disabled])")],
          index = items.indexOf(document.activeElement as HTMLElement);
        const next =
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? items.length - 1
              : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
        items[next]?.focus();
        event.preventDefault();
        return;
      }
      if (event.key !== "Escape") return;
      if (!root.querySelector("dialog[open], [popover]:popover-open") && !target.matches("input,textarea"))
        handlers.dismiss();
      const open = [...root.querySelectorAll<HTMLDetailsElement>("details[open]")].pop();
      if (open) {
        open.open = false;
        open.querySelector<HTMLElement>("summary")?.focus();
      }
    },
    { signal },
  );
  root.addEventListener(
    "dragover",
    (event) => {
      if ((event.target as Element).closest?.(".c1-drop")) event.preventDefault();
    },
    { signal },
  );
  root.addEventListener(
    "drop",
    (event) => {
      const area = (event.target as Element).closest?.(".c1-drop");
      const input = area?.querySelector<HTMLInputElement>("input[type=file]");
      if (!input || input.disabled || !event.dataTransfer?.files?.length) return;
      event.preventDefault();
      input.files = event.dataTransfer.files;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    },
    { signal },
  );
  const place = (panel: HTMLElement) => placeMenu(panel, root);
  // El menú cambia de alto cuando cargan sus opciones: se vuelve a ubicar mientras está abierto.
  const resized = new ResizeObserver((entries) =>
    entries.forEach((entry) => {
      if ((entry.target as HTMLElement).matches(":popover-open")) place(entry.target as HTMLElement);
    }),
  );
  // En el celular una ventana de etiquetas ocupa la pantalla; lo que queda detrás no recibe foco ni toques.
  const sheetInert = (sheet: HTMLElement, on: boolean) => {
    if (!on) {
      document.querySelectorAll<HTMLElement>("[data-c1-sheet-inert]").forEach((item) => {
        item.inert = false;
        delete item.dataset.c1SheetInert;
      });
      return;
    }
    for (let node: HTMLElement | null = sheet; node && node !== document.body; node = node.parentElement)
      for (const sibling of [...(node.parentElement?.children ?? [])] as HTMLElement[])
        if (sibling !== node && !sibling.inert && sibling.localName !== "script") {
          sibling.inert = true;
          sibling.dataset.c1SheetInert = "";
        }
  };
  root.addEventListener(
    "toggle",
    (event) => {
      const target = event.target as HTMLElement;
      const state = (event as ToggleEvent).newState;
      if (target.matches?.(".c1-sheet")) sheetInert(target, state === "open" && small());
      if (!target.matches?.("[data-contact-popover]")) return;
      if (state === "open") {
        place(target);
        resized.observe(target);
      } else resized.unobserve(target);
      if (state === "open") {
        if (fine())
          (
            target.querySelector<HTMLElement>('[aria-checked="true"]') ??
            [...target.querySelectorAll<HTMLElement>("input,button")].find((item) => item.getClientRects().length)
          )?.focus({ preventScroll: true });
        target.scrollTop = 0;
      } else {
        const trigger =
          document.getElementById(target.dataset.contactPopoverTrigger ?? "") ??
          root.querySelector<HTMLElement>(`[popovertarget="${target.id}"]`);
        if (target.contains(document.activeElement) || document.activeElement === document.body)
          (trigger as HTMLElement | null)?.focus();
      }
    },
    { capture: true, signal },
  );
  window.addEventListener(
    "resize",
    () => root.querySelectorAll<HTMLElement>("[data-contact-popover]:popover-open").forEach(place),
    { signal },
  );
  let width = 0;
  const resizeObserver = new ResizeObserver((entries) => {
    const next = Math.round(entries[0]!.contentRect.width);
    if (next === width) return;
    width = next;
    handlers.contentWidth(next, innerWidth);
  });
  resizeObserver.observe(root);
  // Cruzar 768 px cambia qué lista se ve aunque el contenido conserve su ancho.
  matchMedia("(max-width:767px)").addEventListener(
    "change",
    () => {
      if (width) handlers.contentWidth(width, innerWidth);
    },
    { signal },
  );
  // En el celular pide el siguiente lote cuando faltan 10 filas (≈760 px antes del final).
  let seen: Element | null = null;
  const moreObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) handlers.loadMore();
    },
    { rootMargin: "0px 0px 760px 0px" },
  );
  const watchMore = () => {
    const more = root.querySelector("[data-mobile-more]");
    if (more && more !== seen) {
      moreObserver.observe(more);
      seen = more;
    }
  };
  watchMore();
  const moreMutations = new MutationObserver(watchMore);
  moreMutations.observe(root, { childList: true, subtree: true });
  const onScrollMore = () => {
    const more = root.querySelector("[data-mobile-more]");
    if (!more || !small()) return;
    if (more.getBoundingClientRect().top - window.innerHeight < 760) handlers.loadMore();
  };
  window.addEventListener("scroll", onScrollMore, { signal, passive: true });
  document.addEventListener("scroll", onScrollMore, { signal, passive: true, capture: true });
  const dispose = () => {
    surfaceObserver.disconnect();
    restoreInert();
    if (launcher) {
      launcher.style.right = originalRight;
      launcher.hidden = dockHidesLauncher(launcher);
    }
    abort.abort();
    resizeObserver.disconnect();
    resized.disconnect();
    moreObserver.disconnect();
    moreMutations.disconnect();
    if (current?.root === element) current = null;
  };
  current = { root: element, dispose };
  return dispose;
}
